import { isObservableObject, observable } from 'mobx';
import type { Signal } from '@/models/data/Signal';
import { Side } from '@/schemas/tiles';
import type { TileMeta } from '@/models/tiles/TileDefinition';
import { ownVisualAssignment, registerVisualRoot } from '@/utils/OwnedBundleVisualData';

type SideSignals = Record<Side, Signal | null>;
const sides = [Side.TOP, Side.RIGHT, Side.BOTTOM, Side.LEFT] as const;
const constructedMetadata = new WeakSet<object>();

/** Metadata produced here has ordinary observable fields and native identity. */
export function isConstructedTileMeta(value: unknown): value is TileMeta {
  return typeof value === 'object' && value !== null && constructedMetadata.has(value);
}

/** Internal imports/copies own this fresh clone. Preserve assignment hooks, but
 * retain native provenance when the guarded factory constructs its metadata. */
export function ownTileMetaAssignment(root: object, privateClone: TileMeta): TileMeta {
  const meta = ownVisualAssignment(root, 'meta', privateClone);
  if (meta !== privateClone) constructedMetadata.add(meta);
  return meta;
}

function ordinaryDataDescriptors(value: unknown): PropertyDescriptorMap | undefined {
  if (
    value === null ||
    typeof value !== 'object' ||
    Object.getPrototypeOf(value) !== Object.prototype
  )
    return undefined;
  // MobX's ordinary enhancer snapshots descriptors, rather than reading each
  // property. Preserve that behavior for Proxy objects with data descriptors.
  const result: PropertyDescriptorMap = Object.getOwnPropertyDescriptors(value);
  return Reflect.ownKeys(result).every((key) => {
    const descriptor = result[key];
    return (
      'value' in descriptor &&
      descriptor.enumerable &&
      descriptor.configurable &&
      descriptor.writable
    );
  })
    ? result
    : undefined;
}

function ordinarySignalDescriptors(
  source: SideSignals,
): PropertyDescriptorMap | undefined {
  const fields = ordinaryDataDescriptors(source);
  if (!fields || Reflect.ownKeys(fields).length !== 4) return undefined;
  if (
    !sides.every(
      (side) =>
        Object.hasOwn(fields, side) &&
        (fields[side].value === null ||
          fields[side].value === 0 ||
          fields[side].value === 1),
    )
  )
    return undefined;
  return fields;
}

/** Preserve the ordinary MobX field/object observe and intercept contract. */
export function observableSignalSides(source: SideSignals): SideSignals {
  if (isObservableObject(source)) return source;
  const values = ordinarySignalDescriptors(source);
  if (!values) return source;
  // Use the already captured descriptors, preserving source property reads/order.
  const properties = Object.create(Object.prototype, values) as SideSignals;
  const result = observable.object(properties, undefined, { proxy: false });
  return result;
}

/** Construct initial native metadata. Later replacements use ordinary MobX enhancement. */
export function observableTileMeta(source: TileMeta): TileMeta {
  if (isObservableObject(source)) return source;
  const fields = ordinaryDataDescriptors(source);
  if (!fields || !Object.hasOwn(fields, 'inputs') || !Object.hasOwn(fields, 'outputs'))
    return source;
  // Define data fields instead of assigning through Object.prototype setters
  // (notably an own __proto__ field). Symbol metadata remains intact as well.
  const properties = Object.create(Object.prototype, fields) as TileMeta;
  properties.inputs = observableSignalSides(fields.inputs.value);
  properties.outputs = observableSignalSides(fields.outputs.value);
  const meta = observable.object(properties);
  constructedMetadata.add(meta);
  registerVisualRoot(meta);
  return meta;
}
